import { Editor, Element as SlateElement, Node, Path, Range } from 'slate';
import { CustomElement } from '../utils/element-renderer';

type StopMessageElement = Extract<CustomElement, { type: 'stop-message' }>;

function isStopMessage(n: Node): n is StopMessageElement {
  return SlateElement.isElement(n) && 'type' in n && n.type === 'stop-message';
}

/**
 * Helper to get the current STOP message node if it exists
 */
const getStopMessageNode = (editor: Editor): [StopMessageElement, Path] | undefined => {
  const [stopNode] = Array.from(
    Editor.nodes<StopMessageElement>(editor, {
      at: [],
      match: isStopMessage
    })
  );
  return stopNode;
};

/**
 * Check if the current selection intersects with a node
 */
const isSelectionInNode = (editor: Editor, path: Path) => {
  const selection = editor.selection;
  if (!selection) return false;

  const nodeRange = Editor.range(editor, path);
  return Range.intersection(selection, nodeRange) !== null;
};

/**
 * Check if selection is after a node
 */
const isSelectionAfterNode = (editor: Editor, path: Path) => {
  const selection = editor.selection;
  if (!selection) return false;

  const nodeEnd = Editor.end(editor, path);
  return selection.anchor.offset > nodeEnd.offset && selection.anchor.path[0] >= nodeEnd.path[0];
};

/**
 * Plugin to handle STOP message behavior in the editor.
 * Ensures STOP message is always at the end and can't be deleted.
 */
export const withStopMessage = (editor: Editor) => {
  const { deleteBackward, deleteForward, insertBreak } = editor;

  // Prevent deletion of STOP message
  editor.deleteBackward = (unit) => {
    const stopNode = getStopMessageNode(editor);
    if (stopNode && isSelectionInNode(editor, stopNode[1])) {
      return;
    }
    deleteBackward(unit);
  };

  editor.deleteForward = (unit) => {
    const stopNode = getStopMessageNode(editor);
    if (stopNode && isSelectionInNode(editor, stopNode[1])) {
      return;
    }
    deleteForward(unit);
  };

  // Prevent inserting breaks within stop message
  editor.insertBreak = () => {
    const stopNode = getStopMessageNode(editor);
    if (stopNode && isSelectionAfterNode(editor, stopNode[1])) {
      return;
    }
    insertBreak();
  };

  return editor;
};

/**
 * Helper function to create a stop message node
 */
export const createStopMessage = (messageText: string): StopMessageElement => ({
  type: 'stop-message',
  messageText,
  children: [{ text: '' }]
});
